/**
 * RAM — Block / Unblock account.
 *
 * POST /v3/maint/acct/block-unblock
 *
 * Manager review required. The legal basis (subpoena, court order, customer
 * freeze request, fraud hold, etc.) must be captured in the reason field so
 * the approver can validate the action against documented policy.
 *
 * Replaces Wave10ActionFallback for `block-unblock` — the fallback hardcoded
 * `action: 'block'`, so the Unblock path never reached the backend.
 */
import { useState } from 'react';
import { Banner, Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useMaintActionSubmit } from '@/modules/Maint/useMaintActionSubmit';
import type { RamTarget } from '../types';

export interface BlockUnblockProps {
  target: RamTarget;
  onDone: () => void;
  onCancel: () => void;
}

export default function BlockUnblock({
  target,
  onDone,
  onCancel,
}: BlockUnblockProps): JSX.Element {
  const client = useApiClient();
  const { submit, submitting, error } = useMaintActionSubmit();
  const [action, setAction] = useState<'block' | 'unblock'>('block');
  const [statusCode, setStatusCode] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [done, setDone] = useState<boolean>(false);
  const [approvalNote, setApprovalNote] = useState<string | null>(null);

  const reasonOk = reason.trim().length >= 10;
  const canSave = !submitting && Boolean(target.acctId) && reasonOk;

  const handleSave = async (): Promise<void> => {
    if (!canSave || !target.acctId) return;
    const result = await submit(
      (b) => client.retail.maintActions.submitBlockUnblock(b),
      {
        accountNumber: target.acctId,
        action,
        statusCode: statusCode.trim() || undefined,
        reason: reason.trim() || undefined,
      },
    );
    if (!result.success) return;
    if (result.data?.approvalRequired) {
      setApprovalNote(
        `Submitted — awaiting ${result.data.approverRole ?? 'approver'} approval (scenario: ${result.data.approvalScenario ?? 'default'}).`,
      );
    } else if (result.data?.testModeSkipped) {
      setApprovalNote('Test-mode — jXchange call skipped. Audit row recorded.');
    } else {
      setApprovalNote(null);
    }
    setDone(true);
  };

  if (done) {
    return (
      <div className="ram-wizard">
        <Banner tone="ok">
          {action === 'block' ? 'Block' : 'Unblock'} submitted for account {target.acctId}.
        </Banner>
        {approvalNote && <Banner tone="info">{approvalNote}</Banner>}
        <div className="ram-wizard__actions">
          <Button variant="primary" onClick={onDone}>Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="ram-wizard">
      <div className="ram-wizard__header">
        <h2 className="ram-wizard__title">Block / unblock account</h2>
        <Pill variant="info">CIF {target.cifId}</Pill>
        {target.acctId && <Pill variant="muted">Account {target.acctId}</Pill>}
      </div>

      <Banner tone="info">
        Block / unblock requires Manager review. Document the legal basis (subpoena, court order, customer-requested freeze, fraud hold, etc.) in the reason field.
      </Banner>

      <div className="ram-wizard__fields">
        <fieldset className="ram-search__label">
          <legend>Action</legend>
          <label>
            <input
              type="radio"
              name="block-unblock-action"
              value="block"
              checked={action === 'block'}
              onChange={() => setAction('block')}
            />
            {' '}Block
          </label>
          <label>
            <input
              type="radio"
              name="block-unblock-action"
              value="unblock"
              checked={action === 'unblock'}
              onChange={() => setAction('unblock')}
            />
            {' '}Unblock
          </label>
        </fieldset>

        <label className="ram-search__label">
          Status code (optional, e.g. FRZ / LIT / DEC)
          <input
            className="ram-input"
            value={statusCode}
            onChange={(e) => setStatusCode(e.target.value.toUpperCase())}
            maxLength={8}
            placeholder="FRZ"
          />
        </label>

        <label className="ram-search__label">
          Reason (required, min 10 chars)
          <textarea
            className="ram-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            maxLength={512}
          />
        </label>
      </div>

      {error && <Banner tone="err">{error}</Banner>}

      <div className="ram-wizard__actions">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {submitting ? 'Submitting…' : action === 'block' ? 'Submit block' : 'Submit unblock'}
        </Button>
      </div>
    </div>
  );
}
